import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { CircularProgress } from "@heroui/react";

import {
  buildDayRange,
  clampScreentimeDays,
  fetchScreenTime,
  formatScreenTimeDuration,
  intervalBar,
  intervalMinutes,
  mergeDays,
  nowOnDayPercent,
  screentimeDaysDefault,
  ScreenTimeDay,
  ScreenTimeInterval,
  ScreenTimeReport,
  ScreenTimeResult,
  toDayKey,
} from '@/features/screentime/screenTimeApi';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';

import ScreenTimeReset from './ScreenTimeReset';

export interface RoomScreenTimeProps {
  projectId:			string;
  roomNo:				string;
  // Whether the room's RESOLVED settings are active. Decided from the settings
  // we already hold — never from the response, which does not echo the window
  // (so quota_seconds: 0 alone does not prove a room is unconfigured).
  configured:			boolean;
  // Fired with false when the server says the feature must not be shown at all
  // (503 recording off server-wide, or the read refused) so the parent can drop
  // the tab.
  onAvailability?:		(available: boolean) => any;
  onOpenSettings?:		() => any;
  // Erasing history follows the same `enable-screentime` ACL as the rest of the
  // feature; admins are not gated on it.
  canReset?:			boolean;
}

// 35 is the server's ceiling for `days` — it is exactly how long the
// per-minute intervals survive, so there is nothing further back to draw.
const rangeValues = [7, 14, 35];

const cardBorder	= "border-solid border-1 border-[#E7E9EF]";
const barTrack		= "#EDEFF3";
const barViewing	= "#5B7BB4";
const markerColor	= "#D08A2C";

// Quarter-day ticks: 00:00 / 06:00 / 12:00 / 18:00 / 24:00.
const hourTicks = ["00:00", "06:00", "12:00", "18:00", "24:00"];

const Message = (props: {icon: string; title: string; detail?: string; children?: React.ReactNode}) => (
  <FlexRow direction="column"
           align="start"
           className="gap-[8px] py-[24px] max-w-[560px]">
    <FlexRow className="gap-[8px]">
      <span className="material-icons !text-[--Mid-Grey] text-[20px]">
        { props.icon }
      </span>
      <span className="r14 !text-black">
        { props.title }
      </span>
    </FlexRow>
  {props.detail &&
    <span className="text-[--Dark-Grey] text-[12px] leading-[18px] pl-[28px]">
      { props.detail }
    </span>
  }
    { props.children }
  </FlexRow>
);

const Tile = (props: {label: string; children?: React.ReactNode}) => (
  <div className={cx("flex-1 min-w-[170px] rounded-[8px] px-[16px] py-[14px]", cardBorder)}>
    <div className="text-[11px] uppercase tracking-[0.6px] text-[--Dark-Grey] pb-[8px]">
      { props.label }
    </div>
    <div className="text-[20px] font-semibold !text-black leading-[24px]">
      { props.children }
    </div>
  </div>
);

// width="auto" matters: FlexRow defaults to w-full, which makes each legend key
// claim the whole row and squashes the label into a two-line column.
const LegendKey = (props: {swatch: React.ReactNode; label: string}) => (
  <FlexRow flex={0}
           width="auto"
           className="gap-[8px] shrink-0">
    { props.swatch }
    <span className="text-[12px] text-[--Dark-Grey] whitespace-nowrap">
      { props.label }
    </span>
  </FlexRow>
);

// One row = one 24-hour track with the day's viewing periods shaded in place,
// so you can see WHEN the TV was on, not just how much. No charting library
// needed: the API returns decoded intervals precisely so this stays a few divs.
const DayBar = (props: {
  day:			ScreenTimeDay;
  label:		string;
  isToday:		boolean;
}) => {
  const { t } = useTranslation();

  const watched = props.day.seconds > 0;

  return (
  <FlexRow className="gap-[16px] py-[4px]">
    <div className={cx(
          "min-w-[104px] text-right text-[13px]",
          watched ? "!text-black font-medium" : "text-[--Mid-Grey]"
        )}>
      { props.label }
    </div>
    <div className="relative flex-1 h-[30px] rounded-[6px] overflow-hidden"
         style={{ background: barTrack }}>
    {/* Quarter-day gridlines, drawn as gaps in the track. */}
    {[25, 50, 75].map((pct: number) =>
      <div key={pct}
           className="absolute top-0 bottom-0 w-[2px] bg-white"
           style={{ left: `calc(${pct}% - 1px)` }} />
    )}
    {props.day.intervals.map((iv: ScreenTimeInterval, i: number) => {
      const bar = intervalBar(iv);
      if (!bar)
        return null;

      return (
      <div key={i}
           className="absolute top-0 bottom-0 rounded-[6px]"
           title={t('projects:project:screentime:history:interval_n', {
             from:	iv.from,
             to:	iv.to,
             n:		intervalMinutes(iv),
           })}
           style={{
             left:		bar.left + '%',
             width:		bar.width + '%',
             minWidth:	'4px',
             background:	barViewing,
           }} />
      );
    })}
    {/* "Now" only makes sense on today's row. */}
    {props.isToday &&
      <div className="absolute top-0 bottom-0 w-[2px]"
           title={t('projects:project:screentime:history:legend_now')}
           style={{ left: `calc(${nowOnDayPercent()}% - 1px)`, background: markerColor }} />
    }
    </div>
    <div className={cx(
          "min-w-[92px] text-[13px]",
          watched ? "!text-black font-medium" : "text-[--Mid-Grey]"
        )}>
      { formatScreenTimeDuration(props.day.seconds) }
    </div>
  </FlexRow>
  );
};

const RoomScreenTime = (props: RoomScreenTimeProps) => {
  const { t } = useTranslation();

  const [days, setDays]		= React.useState(screentimeDaysDefault);
  const [result, setResult]	= React.useState(null as ScreenTimeResult | null);
  const [loading, setLoading]	= React.useState(true);
  const [reloads, setReloads]	= React.useState(0);

  const onAvailability = props.onAvailability;

  React.useEffect(() => {
    if (	!props.projectId ||
			!props.roomNo
		 )
      return;

    let cancelled = false;

    setLoading(true);

    fetchScreenTime({
      projectId:	props.projectId,
      roomNo:		props.roomNo,
      days:		days,
    }).then((res: ScreenTimeResult) => {
      if (cancelled)
        return;

      setResult(res);
      setLoading(false);

      // Only a server-wide 503 drops the tab. A refusal (403) keeps it and
      // explains itself below — the tab vanishing under the click that opened
      // it leaves the room on a blank panel.
      if (onAvailability)
        onAvailability(res.kind !== "disabled");
    });

    return () => { cancelled = true; };
  }, [props.projectId, props.roomNo, days, reloads, onAvailability]);

  const report =	result && result.kind === "ok"
					?	result.report
					:	null;

  // Recording is switched off server-wide: hide the whole feature rather than
  // draw an empty chart that reads as "this room was never watched".
  if (	result &&
		(	result.kind === "disabled" ||
			result.kind === "unauthorized"
		)
	 )
    return null;

  const dayLabel = (key: string): string => {
    const today		= toDayKey(new Date());
    const yesterday	= toDayKey(new Date(Date.now() - 86400000));

    if (key === today)
      return t('projects:project:screentime:history:today');

    if (key === yesterday)
      return t('projects:project:screentime:history:yesterday');

    const parts = key.split('-');

    return	parts.length === 3
			?	`${parts[2]}.${parts[1]}.${parts[0]}`
			:	key;
  };

  const stats = (rep: ScreenTimeReport) => {
    const hasQuota = rep.quota_seconds > 0;

    return (
    <FlexRow align="start"
             className="gap-[16px] flex-wrap pb-[8px]">
      <Tile label={t('projects:project:screentime:history:period')}>
        { t('projects:project:screentime:period:' + rep.period) }
      </Tile>
      <Tile label={t('projects:project:screentime:history:quota')}>
      {hasQuota
        ?	<React.Fragment>
              { formatScreenTimeDuration(rep.quota_seconds) }
              <span className="text-[13px] font-normal text-[--Dark-Grey]">
                { ' ' + t('projects:project:screentime:history:per_period_n', {
                    period: t('projects:project:screentime:period:' + rep.period).toLowerCase(),
                  }) }
              </span>
            </React.Fragment>
        :	<span className="text-[--Dark-Grey]">
              { t('projects:project:screentime:history:no_quota') }
            </span>
      }
      </Tile>
      <Tile label={t('projects:project:screentime:history:used_in:' + rep.period)}>
        { formatScreenTimeDuration(rep.used_seconds) }
      </Tile>
      <Tile label={t('projects:project:screentime:history:state')}>
        <FlexRow flex={0}
                 className={cx(
                   "gap-[8px] rounded-full px-[12px] py-[4px] w-fit",
                   rep.blocked ? "bg-[#FBECEA]" : "bg-[#EAF5EE]"
                 )}>
          <span className="rounded-full w-[8px] h-[8px]"
                style={{ background: rep.blocked ? '#C0564B' : '#3F9D62' }} />
          <span className={cx("text-[13px] font-medium", rep.blocked ? "text-[#C0564B]" : "text-[#2F7D4E]")}>
          { rep.blocked
            ? t('projects:project:screentime:history:blocked')
            : t('projects:project:screentime:history:not_blocked') }
          </span>
        </FlexRow>
      </Tile>
    </FlexRow>
    );
  };

  const chart = (rep: ScreenTimeReport) => {
    const calendar = mergeDays(buildDayRange(days), rep.days);
    const today = toDayKey(new Date());

    return (
    <FlexRow direction="column"
             align="start"
             className="w-full gap-[2px] pt-[8px]">
      {/* Hour axis. The label columns on either side are spacers so the ticks
          line up with the tracks below. */}
      <FlexRow className="gap-[16px] pb-[4px]">
        <div className="min-w-[104px]" />
        <div className="relative flex-1 h-[16px]">
        {hourTicks.map((tick: string, i: number) =>
          <span key={tick}
                className="absolute text-[11px] text-[--Dark-Grey] -translate-x-1/2 whitespace-nowrap"
                style={{ left: (i * 25) + '%' }}>
            { tick }
          </span>
        )}
        </div>
        <div className="min-w-[92px]" />
      </FlexRow>

    {calendar.map((day: ScreenTimeDay) =>
      <DayBar key={day.day}
              day={day}
              label={dayLabel(day.day)}
              isToday={day.day === today} />
    )}

      <FlexRow className="gap-[24px] pt-[16px]">
        <LegendKey label={t('projects:project:screentime:history:legend_viewing')}
                   swatch={<span className="w-[18px] h-[10px] rounded-[3px] shrink-0"
                                 style={{ background: barViewing }} />} />
        <LegendKey label={t('projects:project:screentime:history:legend_now')}
                   swatch={<span className="w-[2px] h-[14px] shrink-0"
                                 style={{ background: markerColor }} />} />
        <FlexRow />
      </FlexRow>
      <FlexRow className="gap-[8px] pt-[16px]">
        <span className="material-icons !text-[--Mid-Grey] text-[16px]">
          info
        </span>
        <span className="text-[--Dark-Grey] text-[12px]">
          { t('projects:project:screentime:history:retention') }
        </span>
      </FlexRow>
    </FlexRow>
    );
  };

  const body = () => {
    if (loading && !report)
      return (
      <FlexRow className="py-[32px]">
        <CircularProgress aria-label="Loading screen time" size="sm" />
      </FlexRow>
      );

    if (!result)
      return null;

    switch (result.kind) {
    case "no_project_access":
      return <Message icon="lock"
                      title={t('projects:project:screentime:history:no_project_access')} />;

    case "not_found":
      return <Message icon="search_off"
                      title={t('projects:project:screentime:history:not_found')} />;

    case "bad_request":
      return <Message icon="error_outline"
                      title={t('projects:project:screentime:history:bad_request', {status: result.status})} />;

    case "error":
      return (
      <Message icon="cloud_off"
               title={t('projects:project:screentime:history:load_failed')}
               detail={result.message}>
        <FlexRow flex={0}
                 className="pl-[28px] pt-[8px]">
          <Button caption={t('projects:project:screentime:history:retry')}
                  icon="refresh"
                  layout="white"
                  onClick={() => setReloads((n: number) => n + 1)} />
        </FlexRow>
      </Message>
      );
    }

    if (!report)
      return null;

    // An empty `days` array is a normal 200 meaning "nothing was recorded" —
    // not an error, and not the same thing as 503. Which of the two empty
    // states applies is decided from the settings, not from the response.
    const empty = report.days.length === 0;

    return (
    <FlexRow direction="column"
             align="start"
             className="w-full gap-[8px]">
      { stats(report) }

    {/* A local user's project_id is silently forced to their own project —
        never rejected. Echo what the response actually describes. */}
    {report.project_id != '' && report.project_id !== props.projectId &&
      <span className="text-[--Dark-Grey] text-[12px]">
        { t('projects:project:screentime:history:other_project_n', {n: report.project_id}) }
      </span>
    }

    {empty && props.configured &&
      <Message icon="hourglass_empty"
               title={t('projects:project:screentime:history:nothing_recorded')}
               detail={t('projects:project:screentime:history:nothing_recorded_hint')} />
    }

    {empty && !props.configured &&
      <Message icon="tv_off"
               title={t('projects:project:screentime:history:not_configured')}
               detail={t('projects:project:screentime:history:not_configured_hint')}>
      {props.onOpenSettings &&
        <FlexRow flex={0}
                 className="pl-[28px] pt-[8px]">
          <Button caption={t('projects:project:screentime:history:open_settings')}
                  icon="settings"
                  layout="white"
                  onClick={props.onOpenSettings} />
        </FlexRow>
      }
      </Message>
    }

      { !empty && chart(report) }

    {/* Reset lives with the history it destroys. Offered even when `days` is
        empty for THIS range — older rows outside it, or a stale resume point,
        can still be sitting there. */}
    {props.canReset &&
      <FlexRow direction="column"
               align="start"
               className="gap-[10px] w-full pt-[20px] mt-[12px] border-t-1 border-solid border-[#EEF0F4]">
        <ScreenTimeReset projectId={report.project_id || props.projectId}
                         roomNo={report.room_no || props.roomNo}
                         onDone={() => setReloads((n: number) => n + 1)} />
      </FlexRow>
    }
    </FlexRow>
    );
  };

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full gap-[16px] pt-[16px]">
    <span className="text-[24px] font-bold !text-black">
      { t('projects:project:screentime:history:caption') }
    </span>

    <div className={cx("w-full max-w-[1000px] bg-white rounded-[10px] overflow-hidden", cardBorder)}>
      <FlexRow className={cx("gap-[16px] px-[24px] py-[16px] border-b-1 border-solid border-[#E7E9EF]")}>
        <FlexRow flex={0}
                 justify="center"
                 className="w-[40px] h-[40px] rounded-[8px] bg-[#EEF2FA]">
          <span className="material-icons !text-[#5B7BB4] text-[20px]">
            bar_chart
          </span>
        </FlexRow>
        <FlexRow direction="column"
                 align="start">
          <span className="r14 font-semibold !text-black">
            { t('projects:project:screentime:history:title') }
          </span>
          <span className="text-[12px] text-[--Dark-Grey]">
            { t('projects:project:screentime:history:subtitle_n', {n: props.roomNo}) }
          </span>
        </FlexRow>
        <FlexRow flex={0}
                 justify="end"
                 className="gap-[8px]">
        {rangeValues.map((n: number) =>
          <div key={n}
               className={cx(
                 "cursor-pointer select-none rounded-full px-[16px] py-[6px] text-[13px] border-solid border-1 whitespace-nowrap",
                 days === n
                 ?	"border-[--Primetime-main] bg-[--primetime-30] text-[--Primetime-main] font-medium"
                 :	"border-[#E7E9EF] text-[--Dark-Grey]"
               )}
               onClick={() => setDays(clampScreentimeDays(n))}>
            { t('projects:project:screentime:history:range_n', {n: n}) }
          </div>
        )}
        </FlexRow>
      </FlexRow>

      <FlexRow direction="column"
               align="start"
               className="px-[24px] py-[20px]">
        { body() }
      </FlexRow>
    </div>
  </FlexRow>
  );
};

export default RoomScreenTime;
